import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  InputLabel,
  Stack,
  Checkbox,
  FormControlLabel,
  Typography,
  IconButton,
  TextField,
  Paper,
  ClickAwayListener,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import Axios from "axios";
import { base_url } from '../../backendurl/url'
import { Config } from "../../../utils/commonFuctions/Token";

export const MenuSelect = ({
  label,
  placeholder,
  value,
  onChange,
  options,
  name,
  errors,
  validation,
  isDisabled,
  width,
  mode,
}) => {
  const [checkBoxData, setCheckBoxData] = useState(value || {});
  const [selectedSections, setSelectedSections] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});
  const dropdownRef = useRef(null);

  useEffect(() => {
    setCheckBoxData(value || {});
    // Update selected sections based on value
    const selected = [];
    if (value) {
      Object.keys(value).forEach(key => {
        if (value[key] && (value[key].read || value[key].write)) {
          selected.push(key);
        }
      });
    }
    setSelectedSections(selected);
  }, [value]);

  const handleChange = (section, permission) => (event) => {
    const { checked } = event.target;
    const updatedCheckBoxData = {
      ...checkBoxData,
      [section]: {
        ...checkBoxData[section],
        [permission]: checked,
        read: permission === 'read' ? checked : (checkBoxData[section]?.read || false),
        write: permission === 'write' ? checked : (checkBoxData[section]?.write || false),
      },
    };

    // If write is checked, automatically check read
    if (permission === 'write' && checked) {
      updatedCheckBoxData[section].read = true;
    }

    // If read is unchecked, force write to false too
    if (permission === 'read' && !checked) {
      updatedCheckBoxData[section].write = false;
    }

    setCheckBoxData(updatedCheckBoxData);

    const isSelected = Object.values(updatedCheckBoxData[section]).some((perm) => perm);
    let updatedSelectedSections = [...selectedSections];
    
    if (isSelected && !updatedSelectedSections.includes(section)) {
      updatedSelectedSections.push(section);
    } else if (!isSelected) {
      updatedSelectedSections = updatedSelectedSections.filter((s) => s !== section);
    }
    
    setSelectedSections(updatedSelectedSections);
    onChange('menu_permissions', updatedCheckBoxData);
  };

  const toggleSection = (sectionValue) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionValue]: !prev[sectionValue]
    }));
  };

  const handleClickAway = () => {
    setIsOpen(false);
  };

  const toggleDropdown = () => {
    if (mode !== 'view') {
      setIsOpen(!isOpen);
    }
  };

  // const getDisplayValue = () => {
  //   if (selectedSections.length === 0) {
  //     return placeholder || 'Select Menu';
  //   }
  //   return selectedSections.join(', ').toUpperCase();
  // };
  const getDisplayValue = () => {
  if (selectedSections.length === 0) {
    return placeholder || 'Select Menu';
  }

  const labels = [];

  const findLabels = (opts) => {
    opts.forEach((opt) => {
      if (selectedSections.includes(opt.value)) {
        labels.push(opt.label);
      }
      if (opt.children) {
        findLabels(opt.children);
      }
    });
  };

  findLabels(options || []);

  return labels.join(', ');
};

  return (
    <Box sx={{ position: 'relative', width: width || '90%' }}>
      <InputLabel
        htmlFor={name}
        component="div"
        sx={{ 
          fontWeight: 600, 
          fontSize: 16, 
          color: '#000', 
          marginBottom: 1,
          ...(errors?.status && { color: 'red' })
        }}
      >
        {label}
        {validation?.required && (
          <Typography component="span" sx={{ color: 'red', marginLeft: '4px' }}>
            *
          </Typography>
        )}
      </InputLabel>

      <ClickAwayListener onClickAway={handleClickAway}>
        <Box>
          <TextField
            fullWidth
            value={getDisplayValue()}
            placeholder={placeholder || 'Select Menu'}
            disabled={mode === 'view'}
            onClick={toggleDropdown}
            InputProps={{
              readOnly: true,
              endAdornment: (
                <IconButton 
                  size="small" 
                  onClick={toggleDropdown}
                  disabled={mode === 'view'}
                  sx={{ color: 'rgba(0, 0, 0, 0.54)' }}
                >
                  <KeyboardArrowDownIcon 
                    sx={{ 
                      transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 0.2s ease'
                    }} 
                  />
                </IconButton>
              ),
            }}
            sx={{
              cursor: mode === 'view' ? 'default' : 'pointer',
              '& .MuiInputBase-input': {
                cursor: mode === 'view' ? 'default' : 'pointer',
              },
            }}
          />

          {isOpen && (
            <Paper
              ref={dropdownRef}
              elevation={4}
              sx={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                maxHeight: 400,
                overflowY: 'auto',
                zIndex: 1300,
                marginTop: 1,
                border: '1px solid #e0e0e0',
              }}
            >
              {options?.map((data, index) => (
                <Box key={data.value}>
                  {/* Main item */}
                  <Stack 
                    direction="row" 
                    spacing={1}
                    sx={{ 
                      padding: '12px 16px',
                      alignItems: 'center',
                      borderBottom: '1px solid #f5f5f5',
                      '&:hover': {
                        backgroundColor: '#f9f9f9'
                      }
                    }}
                  >
                    <Typography 
                      sx={{ 
                        minWidth: '140px',
                        fontWeight: data.children ? 600 : 400,
                        fontSize: '14px'
                      }}
                    >
                      {data.label}
                    </Typography>
                    
                    {!data.children && (
                      <>
                        <FormControlLabel
                          label="Read"
                          control={
                            <Checkbox
                              size="small"
                              checked={checkBoxData[data.value]?.read || false}
                              onChange={handleChange(data.value, 'read')}
                              sx={{ padding: '4px' }}
                            />
                          }
                          sx={{ 
                            margin: 0,
                            '& .MuiFormControlLabel-label': {
                              fontSize: '13px'
                            }
                          }}
                        />
                       { !['dashboard', 'paymentsreceipts', 'user', 'auditlog', 'servicestatus'].includes(data.value) &&
                       (
                        <FormControlLabel
                          label="Write"
                          control={
                            <Checkbox
                              size="small"
                              checked={checkBoxData[data.value]?.write || false}
                              onChange={handleChange(data.value, 'write')}
                              sx={{ padding: '4px' }}
                            />
                          }
                          sx={{ 
                            margin: 0,
                            '& .MuiFormControlLabel-label': {
                              fontSize: '13px'
                            }
                          }}
                        />)
                       }
                      </>
                    )}
                    
                    {data.children && (
                      <IconButton
                        size="small"
                        onClick={(event) => {
                          event.stopPropagation();
                          toggleSection(data.value);
                        }}
                        sx={{ marginLeft: 'auto', padding: '4px' }}
                      >
                        {expandedSections[data.value] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                    )}
                  </Stack>

                  {/* Sub menu items */}
                  {expandedSections[data.value] && data.children && (
                    <Box sx={{ 
                      backgroundColor: '#fafafa',
                      borderLeft: '3px solid #e3f2fd'
                    }}>
                      {data.children.map((items, subIndex) => (
                        <Stack 
                          direction="row" 
                          spacing={1}
                          key={subIndex} 
                          sx={{ 
                            padding: '10px 16px 10px 32px',
                            alignItems: 'center',
                            borderBottom: subIndex < data.children.length - 1 ? '1px solid #eeeeee' : 'none',
                            '&:hover': {
                              backgroundColor: '#f0f0f0'
                            }
                          }}
                        >
                          <Typography sx={{ 
                            minWidth: '140px', 
                            fontSize: '13px',
                            color: '#666'
                          }}>
                            {items.label}
                          </Typography>
                          <FormControlLabel
                            label="Read"
                            control={
                              <Checkbox
                                size="small"
                                checked={checkBoxData[items.value]?.read || false}
                                onChange={handleChange(items.value, 'read')}
                                sx={{ padding: '4px' }}
                              />
                            }
                            sx={{ 
                              margin: 0,
                              '& .MuiFormControlLabel-label': {
                                fontSize: '12px'
                              }
                            }}
                          />
                          <FormControlLabel
                            label="Write"
                            control={
                              <Checkbox
                                size="small"
                                checked={checkBoxData[items.value]?.write || false}
                                onChange={handleChange(items.value, 'write')}
                                sx={{ padding: '4px' }}
                              />
                            }
                            sx={{ 
                              margin: 0,
                              '& .MuiFormControlLabel-label': {
                                fontSize: '12px'
                              }
                            }}
                          />
                        </Stack>
                      ))}
                    </Box>
                  )}
                </Box>
              ))}
            </Paper>
          )}
        </Box>
      </ClickAwayListener>
    </Box>
  );
};